import React, { useState, useEffect } from 'react';
import { getOperationsDashboardKPIs, getOccupancyHeatmap, getActiveUserSessions, getIncidents, subscribeToIncidents, subscribeToUserSessions, subscribeToInventory, unsubscribeChannel, logActivity } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { Card, CardContent } from '../../components/ui/Card';

import Badge from '../../components/ui/Badge';
import KPICard from '../dashboard/components/KPICard';

const OperationsDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [incidentFilters, setIncidentFilters] = useState({
    severity: '',
    status: '',
    category: ''
  });

  useEffect(() => {
    loadDashboardData();

    // Subscribe to real-time changes
    const incidentsChannel = subscribeToIncidents(() => {
      loadIncidents();
      loadKPIs();
    });

    const sessionsChannel = subscribeToUserSessions(() => {
      loadActiveSessions();
      loadKPIs();
    });

    const inventoryChannel = subscribeToInventory(() => {
      loadKPIs();
      loadHeatmap();
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeChannel(incidentsChannel);
      unsubscribeChannel(sessionsChannel);
      unsubscribeChannel(inventoryChannel);
    };
  }, []);

  useEffect(() => {
    loadIncidents();
  }, [incidentFilters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [kpisData, heatmap, sessions, incidentsData] = await Promise.all([
        getOperationsDashboardKPIs(),
        getOccupancyHeatmap(),
        getActiveUserSessions(),
        getIncidents()
      ]);

      setKpis(kpisData);
      setHeatmapData(heatmap);
      setActiveSessions(sessions);
      setIncidents(incidentsData);

      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'operations_dashboard',
        entityName: 'Operations Dashboard',
        description: 'Viewed operations dashboard'
      });
    } catch (err) {
      console.error('Operations dashboard load error:', err);
      setError(err?.message || 'Failed to load operations dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadKPIs = async () => {
    try {
      const kpisData = await getOperationsDashboardKPIs();
      setKpis(kpisData);
    } catch (err) {
      console.error('Load KPIs error:', err);
    }
  };

  const loadHeatmap = async () => {
    try {
      const heatmap = await getOccupancyHeatmap();
      setHeatmapData(heatmap);
    } catch (err) {
      console.error('Load heatmap error:', err);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const sessions = await getActiveUserSessions();
      setActiveSessions(sessions);
    } catch (err) {
      console.error('Load sessions error:', err);
    }
  };

  const loadIncidents = async () => {
    try {
      const filters = {};
      if (incidentFilters?.severity) filters.severity = incidentFilters?.severity;
      if (incidentFilters?.status) filters.status = incidentFilters?.status;
      if (incidentFilters?.category) filters.category = incidentFilters?.category;

      const incidentsData = await getIncidents(filters);
      setIncidents(incidentsData);
    } catch (err) {
      console.error('Load incidents error:', err);
    }
  };

  const getVacancyColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate >= 50) return 'bg-red-500';
    if (numRate >= 30) return 'bg-orange-500';
    if (numRate >= 15) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getVacancyIntensity = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate >= 50) return 'opacity-100';
    if (numRate >= 30) return 'opacity-75';
    if (numRate >= 15) return 'opacity-50';
    return 'opacity-25';
  };

  const getSeverityVariant = (severity) => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'open': return 'danger';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading operations dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-800">Error: {error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Operations Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live KPI alerts, occupancy heatmap, active sessions, and incident tracking
        </p>
      </div>
      {/* KPI Cards with Live Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Occupancy Rate"
          value={`${kpis?.occupancyRate || 0}%`}
          subtitle={`${kpis?.occupiedUnits || 0} / ${kpis?.totalUnits || 0} units`}
          icon="Building2"
          iconColor={kpis?.alerts?.lowOccupancy ? 'bg-danger-500' : 'bg-success-500'}
          status={kpis?.alerts?.lowOccupancy ? 'error' : 'success'}
          trend={undefined}
          trendValue={undefined}
        />
        <KPICard
          title="Critical Incidents"
          value={kpis?.criticalIncidents || 0}
          subtitle={`${kpis?.openIncidents || 0} open incidents`}
          icon="AlertTriangle"
          iconColor={kpis?.alerts?.criticalIncidents ? 'bg-danger-500' : 'bg-success-500'}
          status={kpis?.alerts?.criticalIncidents ? 'error' : 'success'}
          trend={undefined}
          trendValue={undefined}
        />
        <KPICard
          title="Active Sessions"
          value={kpis?.activeSessions || 0}
          subtitle="Users online now"
          icon="Users"
          iconColor="bg-primary-500"
          trend={undefined}
          trendValue={undefined}
          status={undefined}
        />
        <KPICard
          title="Total Incidents"
          value={kpis?.totalIncidents || 0}
          subtitle={`${kpis?.openIncidents || 0} need attention`}
          icon="Activity"
          iconColor={kpis?.alerts?.highIncidentRate ? 'bg-warning-500' : 'bg-primary-500'}
          status={kpis?.alerts?.highIncidentRate ? 'warning' : undefined}
          trend={undefined}
          trendValue={undefined}
        />
      </div>
      {/* Occupancy Heatmap */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-foreground mb-2">Occupancy Heatmap by Floor</h2>
            <p className="text-sm text-muted-foreground">
              Visual representation of occupancy rates across centers and floors
            </p>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="text-muted-foreground">Vacancy Rate:</span>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 opacity-25 rounded"></div>
                <span>0-15%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 opacity-50 rounded"></div>
                <span>15-30%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 opacity-75 rounded"></div>
                <span>30-50%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 opacity-100 rounded"></div>
                <span>50%+</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {heatmapData?.map((item, index) => (
              <div
                key={index}
                className={`p-6 rounded-lg border-2 transition-all hover:scale-105 ${
                  getVacancyColor(item?.vacancyRate)
                } ${getVacancyIntensity(item?.vacancyRate)}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-white text-lg">{item?.centerName}</h3>
                  <Badge variant="default" className="bg-white text-gray-900">
                    {item?.floor}
                  </Badge>
                </div>
                <div className="space-y-1 text-white">
                  <p className="text-2xl font-bold">{item?.occupancyRate}%</p>
                  <p className="text-sm opacity-90">
                    {item?.occupied} / {item?.total} occupied
                  </p>
                  <p className="text-xs opacity-75">
                    {item?.vacant} vacant units
                  </p>
                </div>
              </div>
            ))}
          </div>

          {heatmapData?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Building2" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No occupancy data available</p>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Active User Sessions */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-foreground mb-2">Active User Sessions</h2>
            <p className="text-sm text-muted-foreground">
              Real-time tracking of logged-in users
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">User</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Center</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Session Start</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Last Activity</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {activeSessions?.map((session) => (
                  <tr key={session?.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                        {session?.userName}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant="primary" size="sm">
                        {session?.userRole}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {session?.centerId || 'All Centers'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatTimestamp(session?.sessionStart)}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatTimestamp(session?.lastActivity)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant="success" dot size="sm">
                        Active
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {activeSessions?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Users" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No active sessions</p>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Incident Log Tracking */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-foreground mb-2">Incident Log</h2>
            <p className="text-sm text-muted-foreground">
              Track and manage incidents across all centers
            </p>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Severity
              </label>
              <select
                value={incidentFilters?.severity}
                onChange={(e) => setIncidentFilters({ ...incidentFilters, severity: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Severities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={incidentFilters?.status}
                onChange={(e) => setIncidentFilters({ ...incidentFilters, status: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <select
                value={incidentFilters?.category}
                onChange={(e) => setIncidentFilters({ ...incidentFilters, category: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Categories</option>
                <option value="Maintenance">Maintenance</option>
                <option value="IT">IT</option>
                <option value="Security">Security</option>
                <option value="Safety">Safety</option>
                <option value="Plumbing">Plumbing</option>
                <option value="Tenant Relations">Tenant Relations</option>
              </select>
            </div>
          </div>

          {/* Incidents Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Severity</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Affected Area</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {incidents?.map((incident) => (
                  <tr key={incident?.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{incident?.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{incident?.description}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={getSeverityVariant(incident?.severity)} size="sm">
                        {incident?.severity}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={getStatusVariant(incident?.status)} size="sm">
                        {incident?.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {incident?.category}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {incident?.affectedArea || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatTimestamp(incident?.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {incidents?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="AlertTriangle" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No incidents found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationsDashboard;